import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { useConfirmEmailVerification, useResendEmailVerification } from '../features/auth/useAuth';
import { getApiErrorMessage } from '../lib/errors';
import { Skeleton } from '../components/ui/Primitives';
import { SkeletonState } from '../components/ui/SkeletonState';
import { AuthShell } from './LoginPage';

const RESEND_COOLDOWN_MS = 2 * 60 * 1000;
const resendStorageKey = (email: string) =>
  `financial.email-verification.resend-after:${email.toLowerCase()}`;

function getInitialResendAt(email: string): number {
  const now = Date.now();
  if (!email) return now + RESEND_COOLDOWN_MS;
  const stored = Number(sessionStorage.getItem(resendStorageKey(email)));
  if (Number.isFinite(stored) && stored > now) return stored;
  const resendAt = now + RESEND_COOLDOWN_MS;
  sessionStorage.setItem(resendStorageKey(email), String(resendAt));
  return resendAt;
}

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isAuth = useAuthStore((s) => s.isAuthenticated());
  const confirm = useConfirmEmailVerification();
  const resend = useResendEmailVerification();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const token = params.get('token');
  const attemptedToken = useRef<string | null>(null);
  const [resendAt, setResendAt] = useState(() => getInitialResendAt(params.get('email') ?? ''));
  const [now, setNow] = useState(Date.now());
  const secondsRemaining = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const canResend = secondsRemaining === 0;
  const resendLabel = useMemo(() => {
    if (canResend) return 'Reenviar enlace';
    const minutes = Math.floor(secondsRemaining / 60);
    const seconds = secondsRemaining % 60;
    return `Podrás reenviar en ${minutes}:${String(seconds).padStart(2, '0')}`;
  }, [canResend, secondsRemaining]);

  useEffect(() => {
    if (!token || attemptedToken.current === token) return;
    attemptedToken.current = token;
    confirm.mutate({ token }, { onSuccess: () => navigate('/cuentas', { replace: true }) });
  }, [confirm, navigate, token]);

  useEffect(() => {
    if (canResend) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [canResend]);

  if (isAuth && !token) return <Navigate to="/panorama" replace />;

  const onResend = (event: FormEvent) => {
    event.preventDefault();
    if (!canResend) return;
    resend.mutate(
      { email },
      {
        onSuccess: () => {
          const nextResendAt = Date.now() + RESEND_COOLDOWN_MS;
          sessionStorage.setItem(resendStorageKey(email), String(nextResendAt));
          setResendAt(nextResendAt);
          setNow(Date.now());
        },
      },
    );
  };

  return (
    <AuthShell
      title="Revisa tu correo"
      subtitle="Te enviamos un enlace de un solo uso, válido durante 24 horas."
    >
      {token && confirm.isPending && (
        <SkeletonState label="Verificando tu correo…" className="space-y-3">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-11 w-full" />
        </SkeletonState>
      )}
      {token && confirm.isError && (
        <p role="alert" className="text-sm text-expense">
          {getApiErrorMessage(
            confirm.error,
            'No pudimos verificar ese enlace. Solicita uno nuevo.',
          )}
        </p>
      )}
      {(!token || confirm.isError) && !confirm.isSuccess && (
        <form onSubmit={onResend} className="mt-4 space-y-3">
          <label htmlFor="resend-email" className="block text-sm font-medium text-slate-700">
            ¿No llegó? Reenvíalo a este correo
          </label>
          <input
            id="resend-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="tap-target w-full rounded-lg border border-slate-300 px-3 py-2"
          />
          {resend.isSuccess && (
            <p className="text-sm text-income">
              Si el correo puede recibir verificación, enviamos un enlace.
            </p>
          )}
          {resend.isError && (
            <p role="alert" className="text-sm text-expense">
              {getApiErrorMessage(resend.error)}
            </p>
          )}
          <button
            type="submit"
            disabled={resend.isPending || !canResend}
            className="button button-secondary w-full"
          >
            {resend.isPending ? 'Enviando…' : resendLabel}
          </button>
        </form>
      )}
      <p className="mt-5 text-center text-sm text-slate-600">
        <Link
          to="/login"
          className="tap-target inline-flex items-center font-semibold text-slate-900 underline"
        >
          Volver a iniciar sesión
        </Link>
      </p>
    </AuthShell>
  );
}
